/**
 * VIN recognition helpers — ported from Link/apps/turbo-mobile's
 * LicenceDiscScanner, with the extraction logic kept verbatim in behaviour and
 * the check digit added as an ADVISORY signal only.
 *
 * Why advisory: the ISO 3779 position-9 check digit is mandatory only in North
 * America. Against the live fleet we tested (9 real VINs supplied by the
 * operator) only 5 validate — the 4 failures are legitimate non-NA vehicles
 * (VW South Africa, Toyota GCC). Gating on the check digit would therefore
 * reject valid VINs for exactly the market this product serves. It is used to
 * RANK competing OCR candidates and to decide whether to ask the user to
 * confirm, never to reject.
 */

/** I, O and Q never appear in a real VIN, so an OCR hit on one is a misread. */
export function ocrCorrectVin(candidate: string): string {
  return candidate.replace(/I/g, '1').replace(/O/g, '0').replace(/Q/g, '0');
}

/** A 17-char run of one character class is noise, not a VIN. */
export function isPlausibleVin(vin: string): boolean {
  const digits = vin.replace(/[^0-9]/g, '').length;
  const letters = vin.replace(/[^A-Z]/g, '').length;
  return digits >= 2 && letters >= 2;
}

const TRANSLIT: Record<string, number> = {
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  E: 5,
  F: 6,
  G: 7,
  H: 8,
  J: 1,
  K: 2,
  L: 3,
  M: 4,
  N: 5,
  P: 7,
  R: 9,
  S: 2,
  T: 3,
  U: 4,
  V: 5,
  W: 6,
  X: 7,
  Y: 8,
  Z: 9,
};
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/** ISO 3779 position-9 check character, or null if the VIN is not decodable. */
export function vinCheckDigit(vin: string): string | null {
  if (vin.length !== 17) return null;
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = vin[i];
    const value = ch >= '0' && ch <= '9' ? Number(ch) : TRANSLIT[ch];
    if (value === undefined) return null;
    sum += value * WEIGHTS[i];
  }
  const rem = sum % 11;
  return rem === 10 ? 'X' : String(rem);
}

/** Advisory only — see the module note. Never use this to reject a VIN. */
export function isCheckDigitValid(vin: string): boolean {
  const expected = vinCheckDigit(vin);
  return expected !== null && expected === vin[8];
}

/** Strip separators and lowercase from typed or pasted input. */
export function normalizeVin(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 17);
}

export type VinSource = 'barcode' | 'label' | 'single-word' | 'two-word' | 'manual';

export interface VinCandidate {
  vin: string;
  source: VinSource;
  /** Advisory ISO 3779 result — informs confirm-vs-accept, never rejection. */
  checkDigitValid: boolean;
}

const VIN_CHARSET = /[^A-HJ-NPR-Z0-9]/g;

function candidate(raw: string, source: VinSource): VinCandidate | null {
  const vin = ocrCorrectVin(raw).replace(VIN_CHARSET, '');
  if (vin.length !== 17 || !isPlausibleVin(vin)) return null;
  return { vin, source, checkDigitValid: isCheckDigitValid(vin) };
}

/**
 * Every 17-char candidate found in an OCR text block, in descending order of
 * trust: source precedence first (a VIN next to the literal word "VIN" beats a
 * loose token), then check-digit-valid ahead of not.
 */
export function extractVinCandidates(text: string): VinCandidate[] {
  const upper = text.toUpperCase();
  const found: VinCandidate[] = [];
  const seen = new Set<string>();
  const push = (c: VinCandidate | null) => {
    if (c && !seen.has(c.vin)) {
      seen.add(c.vin);
      found.push(c);
    }
  };

  // 1. Label-anchored — "VIN" / "S VIN" then the next ~25 alphanumerics. Most
  //    reliable, because the document tells us what the run is.
  const labelRegex = /(?:S\s*)?VIN\s*[:\-.]?\s*([A-Z0-9][A-Z0-9\s]{15,25})/g;
  let m: RegExpExecArray | null;
  while ((m = labelRegex.exec(upper)) !== null) {
    push(candidate(m[1].replace(/\s/g, '').slice(0, 17), 'label'));
  }

  // 2. Loose tokens. Spaces are NOT stripped globally — doing so concatenates
  //    unrelated words into false 17-char matches. Word boundaries are kept and
  //    only adjacent pairs are joined (handles "AAVZZZ6SZ FU026930").
  const words = upper
    .replace(/[^A-Z0-9\s\n]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  for (let i = 0; i < words.length; i++) {
    push(candidate(words[i], 'single-word'));
    if (i + 1 < words.length) push(candidate(words[i] + words[i + 1], 'two-word'));
  }

  const rank: Record<VinSource, number> = {
    barcode: 0,
    manual: 1,
    label: 2,
    'single-word': 3,
    'two-word': 4,
  };
  return found.sort(
    (a, b) =>
      rank[a.source] - rank[b.source] || Number(b.checkDigitValid) - Number(a.checkDigitValid),
  );
}

/** Best candidate, or null. Mirrors turbo-mobile's extractVIN return shape. */
export function extractVin(text: string): VinCandidate | null {
  return extractVinCandidates(text)[0] ?? null;
}

/**
 * VIN field of a South African licence disc PDF417 payload. The disc encodes
 * `%`-delimited fields and the VIN is index 12. Barcode beats OCR outright —
 * it is a digital read, not a visual one.
 */
export function vinFromLicenceDisc(barcodeValue: string): VinCandidate | null {
  const fields = barcodeValue.split('%');
  const raw = fields[12];
  if (!raw) return null;
  const vin = normalizeVin(raw);
  // No OCR correction and no plausibility filter: these bytes were not read
  // visually, so "fixing" an O or rejecting an odd shape would corrupt a
  // reliable value.
  if (vin.length !== 17) return null;
  return { vin, source: 'barcode', checkDigitValid: isCheckDigitValid(vin) };
}
